import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { MapPin, Loader2, DollarSign } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useBrand } from '@/config/brands';
import { useTheme } from '@/contexts/ThemeContext';
import { loadGoogleMapsScript, googleMapsFailed, onGoogleMapsFailure } from '@/lib/googleMapsLoader';
import OpenStreetMapFallback from '@/components/OpenStreetMapFallback';

interface HospitalMapData {
  hospital_id: string;
  hospital_name: string;
  latitude: number;
  longitude: number;
  city: string;
  state: string;
  gross_charge: number;
  inpatient_outpatient?: string;
  rank?: number;
}

interface HospitalPricingMapProps {
  hospitals: HospitalMapData[];
  procedureCode: string;
  codeType: string;
  avgPrice?: number;
}

// --- Helper functions (outside component) ---

function getMarkerColor(grossCharge: number, avgPrice: number): string {
  const percentDiff = avgPrice > 0
    ? ((grossCharge - avgPrice) / avgPrice) * 100
    : 0;
  if (percentDiff <= -25) return '#22c55e';
  if (percentDiff <= 0) return '#84cc16';
  if (percentDiff <= 25) return '#eab308';
  if (percentDiff <= 50) return '#f59e0b';
  if (percentDiff <= 100) return '#f97316';
  return '#dc2626';
}

function buildInfoContent(
  hospital: HospitalMapData,
  color: string,
  avgPrice: number,
  codeType: string,
  procedureCode: string,
  totalCount: number
): string {
  const percentDiff = avgPrice > 0
    ? ((hospital.gross_charge - avgPrice) / avgPrice) * 100
    : 0;
  return `
    <div style="padding: 12px; max-width: 300px; color: #000;">
      <h3 style="margin: 0 0 8px 0; font-size: 16px; font-weight: 600; color: #1f2937;">
        ${hospital.hospital_name}
      </h3>
      <div style="font-size: 14px; color: #6b7280; margin-bottom: 8px;">
        ${hospital.city}, ${hospital.state}
      </div>
      <div style="margin: 8px 0; padding: 8px; background: #f3f4f6; border-radius: 4px;">
        <div style="font-size: 12px; color: #6b7280; margin-bottom: 4px;">
          ${codeType} ${procedureCode} - ${hospital.inpatient_outpatient || 'All Settings'}
        </div>
        <div style="font-size: 20px; font-weight: 700; color: ${color};">
          $${hospital.gross_charge.toLocaleString()}
        </div>
        ${avgPrice > 0 ? `
          <div style="font-size: 12px; margin-top: 4px; color: ${percentDiff > 0 ? '#dc2626' : '#22c55e'};">
            ${percentDiff > 0 ? '+' : ''}${percentDiff.toFixed(1)}% vs avg ($${avgPrice.toLocaleString()})
          </div>
        ` : ''}
      </div>
      ${hospital.rank ? `
        <div style="font-size: 12px; color: #6b7280; margin-top: 8px;">
          Rank: #${hospital.rank} of ${totalCount}
        </div>
      ` : ''}
    </div>
  `;
}

// Google Maps script loading is now in @/lib/googleMapsLoader

export const HospitalPricingMap = React.memo(function HospitalPricingMap({
  hospitals,
  procedureCode,
  codeType,
  avgPrice = 0
}: HospitalPricingMapProps) {
  const navigate = useNavigate();
  const brand = useBrand();
  const { isDark } = useTheme();
  const ahpDark = brand.key === 'ahp' && isDark;
  const mapRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedHospital, setSelectedHospital] = useState<string | null>(null);
  // Flip to the open-source (Leaflet/OSM) fallback if Google Maps auth fails at
  // render time (bad key / referrer not allowed / billing).
  const [gmFailed, setGmFailed] = useState(googleMapsFailed());
  useEffect(() => onGoogleMapsFailure(() => setGmFailed(true)), []);
  const heatmapRef = useRef<google.maps.visualization.HeatmapLayer | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const markerMapRef = useRef<Map<string, google.maps.Marker>>(new Map());
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const rankingsParentRef = useRef<HTMLDivElement>(null);

  // Controls state
  const [maxMarkers, setMaxMarkers] = useState(200);
  const [showExpensive, setShowExpensive] = useState(true);

  // Google Maps API key
  const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';

  // Define gradient colors based on brand
  const heatmapGradient = brand.key === 'antigreed'
    ? [
        'rgba(245, 230, 211, 0)',
        'rgba(245, 230, 211, 0.7)',
        'rgba(144, 238, 144, 0.8)',
        'rgba(34, 139, 34, 0.9)',
        'rgba(0, 64, 0, 1)'
      ]
    : brand.key === 'ahp'
    ? [
        'rgba(0, 40, 104, 0)',
        'rgba(0, 40, 104, 0.5)',
        'rgba(197, 165, 90, 0.7)',
        'rgba(178, 34, 52, 0.85)',
        'rgba(120, 20, 35, 1)'
      ]
    : [
        'rgba(0, 255, 0, 0)',
        'rgba(0, 255, 0, 0.7)',
        'rgba(255, 255, 0, 0.8)',
        'rgba(255, 165, 0, 0.9)',
        'rgba(255, 0, 0, 1)'
      ];

  // Define legend gradient for visual consistency
  const legendGradient = brand.key === 'antigreed'
    ? 'linear-gradient(to right, rgba(255,165,0,0.7), rgba(255,215,0,0.85), rgba(255,100,0,0.95), rgba(255,0,0,1))'
    : brand.key === 'ahp'
    ? 'linear-gradient(to right, rgba(0,40,104,0.7), rgba(197,165,90,0.8), rgba(178,34,52,0.9), rgba(120,20,35,1))'
    : 'linear-gradient(to right, rgba(0,255,0,0.7), rgba(255,255,0,0.8), rgba(255,165,0,0.9), rgba(255,0,0,1))';

  // --- Computed lists ---

  const sortedHospitals = useMemo(() => {
    const sorted = [...hospitals];
    sorted.sort((a, b) =>
      showExpensive ? b.gross_charge - a.gross_charge : a.gross_charge - b.gross_charge
    );
    return sorted;
  }, [hospitals, showExpensive]);

  const validHospitals = useMemo(() =>
    hospitals.filter(h =>
      h.latitude && h.longitude &&
      !isNaN(h.latitude) && !isNaN(h.longitude) &&
      h.latitude !== 0 && h.longitude !== 0
    ), [hospitals]);

  const visibleMarkerHospitals = useMemo(() => {
    const sorted = [...validHospitals];
    sorted.sort((a, b) =>
      showExpensive ? b.gross_charge - a.gross_charge : a.gross_charge - b.gross_charge
    );
    return sorted.slice(0, maxMarkers);
  }, [validHospitals, maxMarkers, showExpensive]);

  // Set of hospital IDs that have markers on the map (for styling)
  const markerHospitalIds = useMemo(() =>
    new Set(visibleMarkerHospitals.map(h => h.hospital_id)),
    [visibleMarkerHospitals]
  );

  // --- Effect 1: Map + Heatmap (runs once when hospitals change) ---
  useEffect(() => {
    const initializeMap = async () => {
      if (!mapRef.current || hospitals.length === 0 || !GOOGLE_MAPS_API_KEY) {
        setLoading(false);
        if (!GOOGLE_MAPS_API_KEY) {
          setError('Google Maps API key not configured');
        }
        return;
      }

      try {
        setLoading(true);
        await loadGoogleMapsScript(GOOGLE_MAPS_API_KEY);

        if (validHospitals.length === 0) {
          setError('No hospitals with valid location data found');
          setLoading(false);
          return;
        }

        // Build bounds from CONUS hospitals only (lat 24–49, lng -125 to -66).
        // Alaska/Hawaii hospitals still render as pins but don't pull the initial
        // framing off-center — without this filter, a single AK pin shifted the
        // continental US to the right edge of the viewport.
        const isConus = (lat: number, lng: number) =>
          lat >= 24 && lat <= 49 && lng >= -125 && lng <= -66;

        const conusHospitals = validHospitals.filter(h => isConus(h.latitude, h.longitude));
        const bounds = new google.maps.LatLngBounds();
        const boundsSource = conusHospitals.length > 0 ? conusHospitals : validHospitals;
        boundsSource.forEach(hospital => {
          bounds.extend(new google.maps.LatLng(hospital.latitude, hospital.longitude));
        });

        // When embedded in the drilldown modal's iframe pane (?embed=1), the viewport is
        // narrow (~40% width). fitBounds in that case can produce odd off-center views.
        // Use a fixed CONUS-centered view at zoom 4 so the user sees the whole country.
        const isEmbedded = typeof window !== 'undefined' &&
          new URLSearchParams(window.location.search).get('embed') === '1';

        const CONUS_CENTER = { lat: 39.8283, lng: -98.5795 };  // geographic center of CONUS
        const mapCenter = isEmbedded
          ? CONUS_CENTER
          : bounds.isEmpty() ? CONUS_CENTER : bounds.getCenter().toJSON();

        const darkMapStyles: google.maps.MapTypeStyle[] = [
          { "elementType": "geometry", "stylers": [{ "color": "#1d2c4d" }] },
          { "elementType": "labels.text.fill", "stylers": [{ "color": "#8ec3b9" }] },
          { "elementType": "labels.text.stroke", "stylers": [{ "color": "#1a3646" }] },
          { "featureType": "water", "elementType": "geometry", "stylers": [{ "color": "#0e1626" }] },
          { "featureType": "administrative", "elementType": "geometry.stroke", "stylers": [{ "color": "#4b6878" }] },
          { "featureType": "road", "elementType": "geometry", "stylers": [{ "color": "#2c3e50" }] },
          { "featureType": "road", "elementType": "geometry.stroke", "stylers": [{ "color": "#1a252f" }] }
        ];

        const ahpMapStyles: google.maps.MapTypeStyle[] = [
          { "elementType": "geometry", "stylers": [{ "color": "#f5f5f5" }] },
          { "elementType": "labels.text.fill", "stylers": [{ "color": "#002868" }] },
          { "elementType": "labels.text.stroke", "stylers": [{ "color": "#ffffff" }] },
          { "featureType": "water", "elementType": "geometry", "stylers": [{ "color": "#c9d7e8" }] },
          { "featureType": "administrative", "elementType": "geometry.stroke", "stylers": [{ "color": "#002868" }, { "weight": 1.2 }] },
          { "featureType": "administrative.province", "elementType": "geometry.stroke", "stylers": [{ "color": "#B22234" }, { "weight": 0.8 }] },
          { "featureType": "road", "elementType": "geometry", "stylers": [{ "color": "#e0e0e0" }] },
          { "featureType": "road", "elementType": "geometry.stroke", "stylers": [{ "color": "#d5d5d5" }] },
          { "featureType": "poi", "elementType": "geometry", "stylers": [{ "color": "#eeeeee" }] },
          { "featureType": "landscape", "elementType": "geometry", "stylers": [{ "color": "#FAF9F6" }] }
        ];

        const mapStyles = brand.key === 'ahp' ? ahpMapStyles : darkMapStyles;

        const googleMap = new google.maps.Map(mapRef.current, {
          center: mapCenter,
          zoom: isEmbedded ? 4 : 5,
          minZoom: 4,
          maxZoom: 18,
          restriction: {
            latLngBounds: { north: 71.5, south: 18.0, west: -180, east: -50 },
            strictBounds: false
          },
          styles: mapStyles
        });

        // Skip fitBounds when embedded — narrow viewport produces off-center views.
        // Embedded users see the whole CONUS at zoom 4 instead.
        if (!isEmbedded) {
          googleMap.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
        }

        google.maps.event.addListenerOnce(googleMap, 'bounds_changed', () => {
          const currentZoom = googleMap.getZoom();
          if (currentZoom !== undefined && currentZoom > 15) {
            googleMap.setZoom(12);
          } else if (currentZoom !== undefined && currentZoom < 4) {
            googleMap.setZoom(5);
          }
        });

        // Create heatmap with ALL valid hospitals
        const maxPrice = Math.max(...validHospitals.map(h => h.gross_charge));
        const minPrice = Math.min(...validHospitals.map(h => h.gross_charge));
        const priceRange = maxPrice - minPrice;

        const heatmapData = validHospitals.map(hospital => {
          const normalizedPrice = priceRange > 0
            ? (hospital.gross_charge - minPrice) / priceRange
            : 0.5;
          const weight = 0.1 + (normalizedPrice * 0.9);
          return {
            location: new google.maps.LatLng(hospital.latitude, hospital.longitude),
            weight: weight * 100
          };
        });

        const heatmap = new google.maps.visualization.HeatmapLayer({
          data: heatmapData,
          map: googleMap,
          radius: 50,
          opacity: 0.7,
          gradient: heatmapGradient
        });

        heatmapRef.current = heatmap;

        // Shared InfoWindow
        infoWindowRef.current = new google.maps.InfoWindow();

        setMap(googleMap);
        setLoading(false);
        setError(null);
      } catch (err) {
        console.error('Error initializing map:', err);
        setError('Failed to load map. Please check your Google Maps API key.');
        setLoading(false);
      }
    };

    initializeMap();

    return () => {
      if (heatmapRef.current) {
        heatmapRef.current.setMap(null);
      }
      markersRef.current.forEach(marker => marker.setMap(null));
      markersRef.current = [];
      markerMapRef.current.clear();
    };
  }, [hospitals, GOOGLE_MAPS_API_KEY]);

  // --- Effect 2: Markers only (re-runs when slider/toggle/map changes) ---
  useEffect(() => {
    if (!map || validHospitals.length === 0) return;

    // Clear old markers
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];
    markerMapRef.current.clear();

    const infoWindow = infoWindowRef.current;
    if (!infoWindow) return;

    visibleMarkerHospitals.forEach((hospital, index) => {
      const color = getMarkerColor(hospital.gross_charge, avgPrice);

      const marker = new google.maps.Marker({
        position: { lat: hospital.latitude, lng: hospital.longitude },
        map: map,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          fillColor: color,
          fillOpacity: 0.9,
          strokeColor: '#ffffff',
          strokeWeight: 1.5,
          scale: 5,
        },
        title: hospital.hospital_name,
        zIndex: 1000 + index,
      });

      marker.addListener('click', () => {
        infoWindow.setContent(
          buildInfoContent(hospital, color, avgPrice, codeType, procedureCode, hospitals.length)
        );
        infoWindow.open(map, marker);
        setSelectedHospital(hospital.hospital_id);
      });

      markersRef.current.push(marker);
      markerMapRef.current.set(hospital.hospital_id, marker);
    });
  }, [map, visibleMarkerHospitals, avgPrice, codeType, procedureCode, hospitals.length]);

  // --- Hospital click handler (works with or without existing marker) ---
  const handleHospitalClick = useCallback((hospital: HospitalMapData) => {
    if (!map) return;

    const existingMarker = markerMapRef.current.get(hospital.hospital_id);
    const infoWindow = infoWindowRef.current;

    if (existingMarker) {
      map.panTo(existingMarker.getPosition()!);
      map.setZoom(8);
      google.maps.event.trigger(existingMarker, 'click');
      setSelectedHospital(hospital.hospital_id);
    } else if (
      hospital.latitude && hospital.longitude &&
      !isNaN(hospital.latitude) && !isNaN(hospital.longitude) &&
      hospital.latitude !== 0 && hospital.longitude !== 0
    ) {
      // Create a temporary highlighted marker for hospitals not in the visible set
      const color = getMarkerColor(hospital.gross_charge, avgPrice);

      const tempMarker = new google.maps.Marker({
        position: { lat: hospital.latitude, lng: hospital.longitude },
        map: map,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          fillColor: '#fbbf24',
          fillOpacity: 1,
          strokeColor: color,
          strokeWeight: 3,
          scale: 8,
        },
        title: hospital.hospital_name,
        zIndex: 9999,
      });

      map.panTo(tempMarker.getPosition()!);
      map.setZoom(8);

      if (infoWindow) {
        infoWindow.setContent(
          buildInfoContent(hospital, color, avgPrice, codeType, procedureCode, hospitals.length)
        );
        infoWindow.open(map, tempMarker);
      }

      // Remove temp marker when info window closes
      if (infoWindow) {
        google.maps.event.addListenerOnce(infoWindow, 'closeclick', () => {
          tempMarker.setMap(null);
        });
      }

      setSelectedHospital(hospital.hospital_id);
    }
  }, [map, avgPrice, codeType, procedureCode, hospitals.length]);

  // Scroll to top when sort direction changes
  useEffect(() => {
    rankingsParentRef.current?.scrollTo({ top: 0 });
  }, [showExpensive]);

  // --- Virtual list ---
  const virtualizer = useVirtualizer({
    count: sortedHospitals.length,
    getScrollElement: () => rankingsParentRef.current,
    estimateSize: () => 56,
    overscan: 10,
  });

  if (hospitals.length === 0) {
    return null;
  }

  const isAhp = brand.key === 'ahp';
  const accentColor = isAhp ? '#002868' : '#22c55e';
  // Heading ink must flip in dark mode (was hardcoded #1B1B1B → dark-on-dark).
  const headingInk = ahpDark ? '#f0ebe3' : '#1B1B1B';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2" style={isAhp ? { fontFamily: "'Instrument Serif', Georgia, serif", color: headingInk } : {}}>
          <MapPin className={`w-5 h-5 ${isAhp ? 'text-[#002868]' : 'text-matrix-green'}`} />
          Hospital Price Heatmap - {codeType} {procedureCode}
        </CardTitle>
        <p className="text-sm text-muted-foreground mt-2">
          Geographic heatmap visualization of hospital pricing intensity.
          {isAhp ? (
            <>
              <span className="text-[#002868] font-medium"> Blue areas</span> = lower prices,
              <span className="text-[#B22234] font-medium"> red areas</span> = higher prices.
            </>
          ) : (
            <>
              <span className="text-green-500"> Green areas</span> = lower prices,
              <span className="text-red-500"> red areas</span> = higher prices.
            </>
          )}
          {' '}Click markers for hospital details.
        </p>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 lg:grid-cols-[2fr,1fr] gap-4">
          {/* Map Section */}
          <div>
            {loading && (
              <div className="flex items-center justify-center h-[600px] bg-secondary/20 rounded-lg">
                <div className="flex flex-col items-center gap-4">
                  <Loader2 className="w-8 h-8 animate-spin text-matrix-green" />
                  <p className="text-muted-foreground">Loading heatmap...</p>
                </div>
              </div>
            )}
            {/* Open-source fallback: Google unavailable (no key / load fail /
                auth failure). Same colored markers, no API key required. */}
            {(error || gmFailed) && (
              <div className="w-full h-[600px] rounded-lg overflow-hidden" style={{ minHeight: '600px' }}>
                <OpenStreetMapFallback
                  hospitals={validHospitals.map(h => ({
                    hospital_id: h.hospital_id, hospital_name: h.hospital_name,
                    latitude: h.latitude, longitude: h.longitude, city: h.city, state: h.state,
                    gross_charge: h.gross_charge,
                  }))}
                  avgPrice={avgPrice}
                  dark={ahpDark}
                  colorFor={getMarkerColor}
                  legend={[
                    { color: '#22c55e', label: '25%+ below avg' },
                    { color: '#84cc16', label: 'Below avg' },
                    { color: '#eab308', label: 'Up to 25% above' },
                    { color: '#f59e0b', label: '25–50% above' },
                    { color: '#f97316', label: '50–100% above' },
                    { color: '#dc2626', label: '2x+ avg' },
                  ]}
                  onSelect={(id) => setSelectedHospital(id)}
                />
              </div>
            )}
            <div
              ref={mapRef}
              className={`w-full h-[600px] rounded-lg ${loading || error || gmFailed ? 'hidden' : ''}`}
              style={{ minHeight: '600px' }}
            />
          </div>

          {/* Rankings Panel — shown alongside the map (Google or OSM fallback). */}
          {!loading && (
            <div className="bg-secondary/20 rounded-lg flex flex-col h-[600px]">
              {/* Controls Bar */}
              <div className="p-3 border-b border-border/50 space-y-3">
                {/* Toggle: Most Expensive / Cheapest */}
                <div className="flex rounded-lg overflow-hidden border border-border/50">
                  <button
                    onClick={() => setShowExpensive(true)}
                    className="flex-1 text-xs font-semibold py-1.5 px-2 transition-colors"
                    style={showExpensive ? { backgroundColor: accentColor, color: '#fff' } : {}}
                  >
                    Most Expensive
                  </button>
                  <button
                    onClick={() => setShowExpensive(false)}
                    className="flex-1 text-xs font-semibold py-1.5 px-2 transition-colors"
                    style={!showExpensive ? { backgroundColor: accentColor, color: '#fff' } : {}}
                  >
                    Cheapest
                  </button>
                </div>

                {/* Slider */}
                <div className="space-y-1">
                  <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                    <span>Map pins</span>
                    <span className="font-mono font-semibold">{maxMarkers}</span>
                  </div>
                  <Slider
                    value={[maxMarkers]}
                    onValueChange={(v) => setMaxMarkers(v[0])}
                    min={50}
                    max={Math.min(2000, validHospitals.length)}
                    step={50}
                  />
                </div>

                <p className="text-[11px] text-muted-foreground">
                  Showing {Math.min(maxMarkers, validHospitals.length).toLocaleString()} of {validHospitals.length.toLocaleString()} on map
                </p>
              </div>

              {/* Column header */}
              <div className="grid grid-cols-[2rem,1fr,4.5rem] gap-1 px-3 py-2 text-[11px] font-semibold text-muted-foreground border-b border-border/50">
                <span>#</span>
                <span>Hospital</span>
                <span className="text-right">Price</span>
              </div>

              {/* Virtualized rows */}
              <div
                ref={rankingsParentRef}
                className="flex-1 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
              >
                <div
                  style={{ height: `${virtualizer.getTotalSize()}px`, width: '100%', position: 'relative' }}
                >
                  {virtualizer.getVirtualItems().map((virtualRow) => {
                    const hospital = sortedHospitals[virtualRow.index];
                    const rank = virtualRow.index + 1;
                    const hasMarker = markerHospitalIds.has(hospital.hospital_id);
                    const isSelected = selectedHospital === hospital.hospital_id;

                    return (
                      <div
                        key={`${hospital.hospital_id}-${virtualRow.index}`}
                        data-index={virtualRow.index}
                        ref={virtualizer.measureElement}
                        className={`absolute left-0 w-full grid grid-cols-[2rem,1fr,4.5rem] gap-1 items-center px-3 py-2 cursor-pointer transition-colors hover:bg-secondary/40 border-b border-border/20 ${
                          isSelected ? 'bg-primary/10 border-l-2' : ''
                        } ${!hasMarker ? 'opacity-60' : ''}`}
                        style={{
                          transform: `translateY(${virtualRow.start}px)`,
                          borderLeftColor: isSelected ? accentColor : 'transparent',
                        }}
                        onClick={() => handleHospitalClick(hospital)}
                      >
                        {/* Rank */}
                        <div>
                          <Badge
                            variant={rank === 1 ? 'destructive' : rank <= 3 ? 'default' : 'secondary'}
                            className="text-[10px] px-1.5 py-0"
                          >
                            {rank}
                          </Badge>
                        </div>

                        {/* Hospital info */}
                        <div className="min-w-0">
                          <div className="text-xs font-medium truncate" title={hospital.hospital_name}>
                            {hospital.hospital_name}
                          </div>
                          <div className="text-[10px] text-muted-foreground truncate">
                            {hospital.city}, {hospital.state}
                          </div>
                        </div>

                        {/* Price + action */}
                        <div className="text-right">
                          <div className="font-mono text-xs font-semibold">
                            ${hospital.gross_charge.toLocaleString()}
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-5 text-[9px] px-1 mt-0.5"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/hospital/${hospital.hospital_id}`);
                            }}
                            title="Open hospital prices"
                          >
                            <DollarSign className="h-3 w-3 mr-0.5" />
                            Prices
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Legend */}
        {!loading && !error && (
          <div className="mt-4 p-4 bg-secondary/20 rounded-lg">
            <h4 className="text-sm font-semibold mb-3" style={isAhp ? { fontFamily: "'Instrument Serif', Georgia, serif", color: headingInk } : {}}>Heatmap Intensity Guide</h4>
            <div className="flex items-center gap-2 mb-3">
              <div
                className="h-6 w-full rounded"
                style={{ background: legendGradient }}
              ></div>
            </div>
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Lower Prices (Better Value)</span>
              <span>Higher Prices (More Expensive)</span>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-4">
              <div>
                <p className="text-xs font-semibold mb-2">Price Range</p>
                <p className="text-sm">
                  ${Math.min(...hospitals.map(h => h.gross_charge)).toLocaleString()} -
                  ${Math.max(...hospitals.map(h => h.gross_charge)).toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold mb-2">Average Price</p>
                <p className="text-sm">${avgPrice.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs font-semibold mb-2">Median Price</p>
                <p className="text-sm">
                  ${(() => {
                    const sorted = [...hospitals].sort((a, b) => a.gross_charge - b.gross_charge);
                    const mid = Math.floor(sorted.length / 2);
                    return sorted.length % 2 === 0
                      ? Math.round((sorted[mid - 1].gross_charge + sorted[mid].gross_charge) / 2).toLocaleString()
                      : sorted[mid].gross_charge.toLocaleString();
                  })()}
                </p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
});
